import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Chip,
    Search,
    SearchItem,
    makeStyles
} from '@ellucian/react-design-system/core';
import {
    spacing20,
    spacing30,
    spacing40,
    colorGlobalBorderError,
    borderWidthThick,
    sizingXSmall,
} from '@ellucian/react-design-system/core/styles/tokens';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import identity from 'lodash/identity';
import head from 'lodash/head';
import toPath from 'lodash/toPath';
import { batch, useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { validateField, setTouched, setFocused, setValue } from '@nsr/react-form';
import { useIntl } from 'react-intl';
import useRendersCount from '@hedtech/userenderscount';

const useStyles = makeStyles(() => ({
    chips: {
        display: 'flex',
        paddingTop: spacing40,
        flexWrap: 'wrap',
    },
    chipDiv: {
        paddingRight: spacing30,
        paddingBottom: spacing30,
    },
    errorSearch: {
        border: 'solid',
        borderColor: colorGlobalBorderError,
        borderWidth: borderWidthThick,
        borderRadius: sizingXSmall,
    },
    helperText: {
        paddingTop: spacing20,
    },
}));

const FormSearch = props => {
    const { formatMessage } = useIntl();
    const classes = useStyles();
    const {
        formatFromStore = identity,
        formatToStore = identity,
        dynamicValues = false,
        fullWidth,
        helperText: helperTextProp = null,
        initialValue,
        label,
        maxSelections,
        name: nameProp,
        onBlur: onBlurProp,
        onFocus: onFocusProp,
        options = [],
        onSearch,
        onSelect,
        onDeleteItem,
        required,
        requiredErrorTextId = 'validateRequired',
        searchDelay,
        validate: validateProp,
        maxSearchOptionsWarningText,
        multiSearchAndSelectMode = true,
        initialSearchValue,
        onClearSearchBox,
        disabled
    } = props;
    useRendersCount(`FormSearchField( ${nameProp} )`);

    const dispatch = useDispatch();
    const selectAlwaysShowError = useMemo(() => {
        return createSelector(
            state => get(state.form.meta, `forceShowErrors.${head(toPath(nameProp))}`),
            value => !!value
        );
    }, [nameProp]);
    const selectValue = useMemo(() => {
        return createSelector(
            state => get(state.form.values, toPath(nameProp)),
            value => value ? formatFromStore(value) : '',
        );
    }, [nameProp, formatFromStore]);
    const selectError = useMemo(() => {
        return createSelector(
            state => get(state.form.errors, toPath(nameProp)),
            identity
        );
    }, [nameProp]);
    const selectTouched = useMemo(() => {
        return createSelector(
            state => get(state.form.touched, toPath(nameProp)),
            identity
        );
    }, [nameProp]);

    const selectSearchValues = useMemo(() => {
        return createSelector(
            state => get(state.form.values, toPath(nameProp)),
            (value = []) => {
                return value;
            }
        );
    }, [nameProp]);
    const value = useSelector(selectValue);
    const error = useSelector(selectError);
    const touched = useSelector(selectTouched);
    const alwaysShowError = useSelector(selectAlwaysShowError);

    const validate = useMemo(() => {
        const isRequired = value => {
            if (!required) {
                return undefined;
            }
            if (!value || !value?.length) {
                return formatMessage({ id: requiredErrorTextId });
            }
        };
        const composeValidators = (fns = []) => (value, allValues, meta) => {
            const validators = [isRequired, ...fns];
            let error;
            for (let x = 0; x < validators.length; x++) {
                error = validators[x](value, allValues, meta);
                if (error) {
                    break;
                }
            }
            return error;
        };
        let validatorsArray = validateProp;
        if (validateProp && typeof validateProp === 'function') {
            validatorsArray = [validateProp];
        }
        return composeValidators(validatorsArray);
    }, [formatMessage, required, validateProp, requiredErrorTextId]);

    const [searchString, setSearchString] = useState(initialSearchValue ? initialSearchValue : '');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [maxLimitError, setMaxLimitError] = useState(false);
    const [liveRegionMessage, setLiveRegionMessage] = useState('');
    const formValues = useSelector(selectSearchValues);
    const [selectedItems, setSelectedItems] = useState(() => {
        const keyedOptions = keyBy(options, 'value');
        if (initialValue && initialValue.length) {
            return initialValue.map(it => {
                if (keyedOptions[it]) {
                    return { value: it, label: keyedOptions[it].label, code: keyedOptions[it].code};
                }
            });
        }
        return multiSearchAndSelectMode ? formValues?.map(it => {
            if (keyedOptions[it]) {
                return { value: it, label: keyedOptions[it].label, code: keyedOptions[it].code };
            } else {
                return [];
            }
        }) : formValues.value ? [formValues] : [];
    });

    useEffect(() => {
        dispatch(setValue({
            name: nameProp,
            value: formatToStore(selectedItems.map(item => item.value)),
            code: formatToStore(selectedItems.map(item => item.code))
        }));
    }, [selectedItems]);

    useEffect(() => {
        setSearchString(initialSearchValue);
    }, [initialSearchValue]);

    useEffect(() => {
        dispatch(validateField({ name: nameProp, validate, value }));
    }, [dispatch, nameProp, validate, value]);

    useEffect(() => {
        const el = (e) => {
            onFocus(e);
        };
        const searchInput = document.getElementById(nameProp);
        searchInput.addEventListener('focus', el);
        return function () {
            searchInput.removeEventListener('focus', el);
        };
    }, [nameProp]);

    useEffect(() => {
        const el = (e) => {
            onBlur(e);
        };
        const searchInput = document.getElementById(nameProp);
        searchInput.addEventListener('blur', el);
        return function () {
            searchInput.removeEventListener('blur', el);
        };
    }, [nameProp]);

    const onChange = (event) => {
        setSearchString(event.target.value);
        if (!multiSearchAndSelectMode && !event.target.value) {
            onClear();
        }
    };

    const onFilter = async (value) => {
        setLoading(true);
        setLiveRegionMessage('Loading');
        try {
            if (dynamicValues) {
                let result = [];
                if (onSearch) {
                    result = await onSearch(value);
                }
                if(result.length) {
                    setSearchResults(result);
                    setLiveRegionMessage(formatMessage({id: 'searchResultsFoundTextId'}, { count: result.length }));
                } else{
                    setSearchResults([]);
                    setLiveRegionMessage(formatMessage({id: 'noSearchResultsTextId'}));
                }
            }
            else {
                const optionsTemp = options.filter((option) => {
                    if (option.label) {
                        return option.label.toLowerCase().includes(value.toLowerCase());
                    }
                });
                if (optionsTemp.length) {
                    setSearchResults(optionsTemp);
                    setLiveRegionMessage(formatMessage({id: 'searchResultsFoundTextId'}, { count: optionsTemp.length }));
                } else {
                    setSearchResults([]);
                    setLiveRegionMessage(formatMessage({id: 'noSearchResultsTextId'}));
                }
            }
        } catch (error) {
            setSearchResults([]);
            setLiveRegionMessage(formatMessage({id: 'noSearchResultsTextId'}));
        }
        setLoading(false);
    };

    const onClear = () => {
        setSearchString('');
        setSearchResults([]);
        setMaxLimitError(false);
        setLiveRegionMessage('');
        if (!multiSearchAndSelectMode) {
            setSelectedItems([]);
        }
        onClearSearchBox && onClearSearchBox();
    };

    const onItemSelect = (event, index) => {
        if (searchResults[index].label) {
            setSearchString(searchResults[index].label);
        }
        const items = [...selectedItems];
        if (!multiSearchAndSelectMode) {
            setSelectedItems([searchResults[index]]);
            onSelect && onSelect(searchResults[index]);

        } else if ((!selectedItems.length || selectedItems.length < maxSelections || !maxSelections) &&
            !items.some(i => i.value === searchResults[index].value)) {
            items.push(searchResults[index]);
            setSelectedItems(items);
            onSelect && onSelect(searchResults[index]);
        }
        else if (selectedItems.length === maxSelections) {
            setMaxLimitError(true);
        }
    };

    const handleDelete = (i) => {
        const items = [...selectedItems];
        const item = items.splice(i, 1);
        setSelectedItems(items);
        onDeleteItem && onDeleteItem(item?.[0]);
    };

    const onBlur = useCallback(e => {
        batch(() => {
            dispatch(setTouched({ name: nameProp, value: true }));
            dispatch(setFocused({ name: nameProp, value: false }));
        });
        onBlurProp && onBlurProp(e);
    }, [dispatch, onBlurProp]);
    const onFocus = useCallback(e => {
        dispatch(setFocused({ name: nameProp, value: true }));
        onFocusProp && onFocusProp(e);
    }, [dispatch, onFocusProp]);
    let hasError = false;
    let helperText = helperTextProp;
    if (error && (touched || alwaysShowError)) {
        hasError = true;
        helperText = error;
    }
    return (
        <div>
            <div>
                <Search
                    id={nameProp}
                    onChange={onChange}
                    onClear={onClear}
                    disabled={disabled}
                    value={searchString}
                    label={required ? `${label} *` : label}
                    aria-label={required ? formatMessage({id: 'searchField'}, {value: label}) + ' ' + formatMessage({ id: requiredErrorTextId }) : formatMessage({id: 'searchField'}, {value: label})}
                    fullWidth={fullWidth}
                    onKeyDown={onBlur}
                    error={hasError || maxLimitError}
                    helperText={hasError ? helperText : maxLimitError ? maxSearchOptionsWarningText : helperText}
                    typeaheadOptions={{
                        loading: loading,
                        onFilter: onFilter,
                        onItemSelect: onItemSelect,
                        searchDelay: searchDelay || 0,
                    }}
                    inputProps={{
                        role: 'combobox', 
                        'aria-haspopup': 'listbox',
                        'aria-controls': `${nameProp}-listbox`, 
                        'aria-live': 'polite'
                    }}
                >
                    {
                        searchResults?.map((data) => {
                            return (
                                <SearchItem
                                    key={data.value}
                                    divider={true}
                                    option={data}
                                >
                                    {data.label}
                                </SearchItem>
                            );
                        })
                    }
                </Search>
                <div aria-live="polite" style={{ position: 'absolute', left: '-9999px' }}>
                    {liveRegionMessage}
                </div>
            </div>
            {multiSearchAndSelectMode &&
                <div className={classes.chips}>
                    {selectedItems.map((item, idx) => {
                        return (
                            <div key={item.value} className={classes.chipDiv}>
                                <Chip
                                    label={item.label}
                                    aria-label={item.label}
                                    onDelete={() => handleDelete(idx)}
                                />
                            </div>
                        );
                    })}
                </div>
            }
        </div>
    );
};

FormSearch.propTypes = {
    formatFromStore: PropTypes.func,
    formatToStore: PropTypes.func,
    alwaysShowError: PropTypes.bool,
    dynamicValues: PropTypes.bool,
    fullWidth: PropTypes.bool,
    helperText: PropTypes.string,
    initialValue: PropTypes.array,
    onSearch: PropTypes.func,
    onSelect: PropTypes.func,
    options: PropTypes.array,
    label: PropTypes.string.isRequired,
    maxSelections: PropTypes.number,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    requiredErrorTextId: PropTypes.string,
    searchDelay: PropTypes.number,
    validate: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
    maxSearchOptionsWarningText: PropTypes.string,
    multiSearchAndSelectMode: PropTypes.bool,
    initialSearchValue: PropTypes.string,
    onClearSearchBox: PropTypes.func,
    disabled: PropTypes.string,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onDeleteItem: PropTypes.func
};

export default FormSearch;
